/**
 * CapitalCoordinatesChapter.jsx — GRANULLO_NEED_HAS_AN_ADDRESS_V4
 *
 * Single-scenario focus, mirror of the HistoricalTimeline pattern:
 *  1. User picks a scenario (tab row up top)
 *  2. Everything on the page is about that one scenario —
 *     the map zooms to it, the 4-step flow walks through it,
 *     the story panel reveals the details of ONLY that story.
 *
 * No competing hover interactions on the map. One story at a time.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, MapPin, Shield, AlertTriangle, Play, Pause, RotateCcw } from 'lucide-react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { ATLAS_REGIONS, getNeedColor, getUrgencyPulse } from '../../data/atlasData';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
const S = '#CD2200'; // scarlet (trapped / today)
const E = '#10B981'; // emerald (verified / with granullo)
const G = '#F5A623'; // amber (intervention)

const ISO_NUMERIC_MAP = {
  '356': 'IN', '404': 'KE', '566': 'NG', '050': 'BD', '076': 'BR',
  '231': 'ET', '180': 'CD', '360': 'ID', '608': 'PH', '586': 'PK',
  '104': 'MM', '170': 'CO', '834': 'TZ', '320': 'GT', '508': 'MZ',
};

const TOTAL_TRAPPED = ATLAS_REGIONS.reduce((sum, r) => {
  const num = parseFloat(r.trappedCapital?.replace(/[^0-9.]/g, '') || '0');
  return sum + num;
}, 0);
const NO_NODE_COUNT = ATLAS_REGIONS.filter(r => r.needsNode).length;
const TOTAL_NODES = ATLAS_REGIONS.reduce((sum, r) => sum + r.verifiedNodes, 0);

const STORIES = [
  {
    id: 'rajasthan',
    tabLabel: 'Rajasthan Water',
    title: 'Rajasthan Water Resilience',
    route: 'London → Rajasthan',
    flag: '🇮🇳',
    countryISO: 'IN',
    sector: 'Water',
    outcomeTag: '$2.8M saved',
    coordinates: [73.7, 26.9],
    need: '230,000 farmers identified · $4.2M budget allocated',
    contradiction: 'No village water-point coordinates',
    resolution: '1,842 village water points mapped. Funds released only after borewell completion proof.',
    outcome: 'Verified delivery: 96% · Leakage prevented: $2.8M',
    trappedDelta: 2.8,
    nodesDelta: 1842,
  },
  {
    id: 'turkana',
    tabLabel: 'Kenya School Meals',
    title: 'Kenya School Meals',
    route: 'Singapore → Turkana',
    flag: '🇰🇪',
    countryISO: 'KE',
    sector: 'Education',
    outcomeTag: '2.1M meals served',
    coordinates: [35.5, 3.1],
    need: '340 schools identified · $1.8M meals budget',
    contradiction: 'No attendance verification system',
    resolution: '340 schools onboarded with digital attendance. Meal provider paid per verified day.',
    outcome: 'Meals served: 2.1M · Ghost-school fraud prevented: $420K',
    trappedDelta: 0.42,
    nodesDelta: 340,
  },
  {
    id: 'dhaka',
    tabLabel: 'Bangladesh Girls Ed',
    title: 'Bangladesh Girls Education',
    route: 'Dubai → Dhaka',
    flag: '🇧🇩',
    countryISO: 'BD',
    sector: 'Scholarships',
    outcomeTag: '89% retention',
    coordinates: [90.4, 23.8],
    need: '12,000 scholarships · $3.6M tuition budget',
    contradiction: 'No student presence proof at institution',
    resolution: 'Biometric attendance + school accreditation verified. Tuition released per quarter.',
    outcome: 'Retention improved: 89% · Diversion prevented: $1.1M',
    trappedDelta: 1.1,
    nodesDelta: 850,
  },
];

const FLOW_STEPS = [
  { key: 'pledged',      label: 'Pledged',      color: 'var(--shell-text)', icon: '◼', blurb: 'Budget announced' },
  { key: 'trapped',      label: 'Trapped',      color: S,                   icon: '✕', blurb: 'No arrival coordinates' },
  { key: 'intervention', label: 'Intervention', color: G,                   icon: '◐', blurb: 'Nodes mapped' },
  { key: 'verified',     label: 'Verified',     color: E,                   icon: '✓', blurb: 'Proof of arrival' },
];

export default function CapitalCoordinatesChapter() {
  const navigate = useNavigate();
  const [storyIdx, setStoryIdx] = useState(0);
  const [stage, setStage] = useState(-1);
  const [animTrapped, setAnimTrapped] = useState(0);
  const [animNodes, setAnimNodes] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const story = STORIES[storyIdx];

  const runLoop = useCallback(() => {
    setStage(-1);
    setAnimTrapped(0);
    setAnimNodes(0);
    let s = -1;
    const iv = setInterval(() => {
      if (paused) return;
      s++;
      if (s >= 4) {
        clearInterval(iv);
        // Hold the verified state — don't auto-jump. User controls the tab row.
      } else {
        setStage(s);
        if (s === 1) setAnimTrapped(story.trappedDelta);
        if (s === 2) setAnimNodes(story.nodesDelta);
        if (s === 3) setAnimTrapped(0);
      }
    }, 2200);
    return () => clearInterval(iv);
  }, [story.trappedDelta, story.nodesDelta, paused]);

  useEffect(() => {
    const cleanup = runLoop();
    return () => { cleanup(); if (timerRef.current) clearTimeout(timerRef.current); };
  }, [storyIdx, runLoop]);

  const stageColor = stage === 1 ? S : stage === 2 ? G : stage === 3 ? E : 'var(--shell-text-muted)';

  const selectStory = (i) => {
    setPaused(false);
    setStoryIdx(i);
  };

  const replay = () => {
    setPaused(false);
    runLoop();
    setStoryIdx(storyIdx); // trigger effect cleanup + restart
    setStage(-1);
    setAnimTrapped(0);
    setAnimNodes(0);
  };

  return (
    <section className="py-20 sm:py-28 px-6 lg:px-8" data-testid="capital-coordinates-chapter">
      <div className="max-w-[1120px] mx-auto">

        {/* ═══ HEADER ═══ */}
        <div className="mb-10 grid lg:grid-cols-[1fr_minmax(0,360px)] gap-10 items-end">
          <div className="max-w-[680px]">
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] mb-4" style={{ color: S }}>
              <MapPin size={11} style={{ display: 'inline', verticalAlign: -2, marginRight: 6 }} />
              Capital Intelligence · Live Atlas
            </p>
            <h2 className="text-[28px] sm:text-[36px] lg:text-[44px] leading-[1.06] tracking-[-0.03em] mb-4"
              style={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: 'var(--shell-text)' }}
              data-testid="cwc-chapter-heading">
              Need has an address.{' '}
              <span style={{ color: S }}>Capital should too.</span>
            </h2>
            <p className="text-[14px] sm:text-[15px] leading-[1.6]" style={{ color: 'var(--shell-text-secondary)' }}>
              Billions in CSR and philanthropic capital deploy every year without coordinates.
              Granullo maps <em>where</em> need lives — and releases capital only when proof of arrival is structural.
            </p>
          </div>

          {/* 3 headline impact numbers */}
          <div className="grid grid-cols-3 gap-3" data-testid="capital-impact-cards">
            {[
              { big: `$${Math.round(TOTAL_TRAPPED)}M`, label: 'trapped in the field', accent: S },
              { big: `${TOTAL_NODES}`, label: 'verified nodes live', accent: E },
              { big: `${NO_NODE_COUNT}`, label: 'no-node gaps', accent: G },
            ].map((s, i) => (
              <div key={i} className="relative overflow-hidden rounded-md px-3 py-3"
                style={{ background: 'var(--shell-surface)', border: '1px solid var(--shell-border)' }}
              >
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: s.accent, opacity: 0.9 }} />
                <p style={{ fontFamily: "'SF Mono', monospace", fontSize: 20, fontWeight: 700, color: s.accent, lineHeight: 1, marginBottom: 4, letterSpacing: '-0.01em' }}>
                  {s.big}
                </p>
                <p style={{ fontSize: 10, color: 'var(--shell-text-muted)', lineHeight: 1.4 }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ═══ SCENARIO TAB ROW — ONLY way to switch stories ═══ */}
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="scenario-tabs">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] mr-2" style={{ color: 'var(--shell-text-muted)' }}>
            Pick a scenario
          </span>
          {STORIES.map((st, i) => {
            const active = i === storyIdx;
            return (
              <motion.button
                key={st.id}
                onClick={() => selectStory(i)}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[12px] font-semibold cursor-pointer"
                style={{
                  background: active ? S : 'var(--shell-surface)',
                  color: active ? '#fff' : 'var(--shell-text)',
                  border: `1px solid ${active ? S : 'var(--shell-border)'}`,
                  boxShadow: active ? `0 0 18px ${S}33` : 'none',
                  transition: 'all 220ms ease',
                }}
                data-testid={`scenario-tab-${st.id}`}
              >
                <span style={{ fontSize: 13 }}>{st.flag}</span>
                {st.tabLabel}
                <span className="text-[9px] font-mono uppercase tracking-[0.12em]" style={{ opacity: active ? 0.75 : 0.45 }}>
                  · {st.sector}
                </span>
                {/* Outcome microtag — always visible, same tab */}
                <span
                  className="inline-flex items-center text-[10px] font-mono font-semibold tabular-nums ml-1 pl-2 py-0.5 rounded-full"
                  style={{
                    color: active ? '#fff' : E,
                    background: active ? 'rgba(255,255,255,0.14)' : `${E}10`,
                    borderLeft: active ? '1px solid rgba(255,255,255,0.2)' : `1px solid ${E}35`,
                    paddingRight: 8,
                  }}
                >
                  ✓ {st.outcomeTag}
                </span>
              </motion.button>
            );
          })}
          {/* Replay / play-pause */}
          <div className="ml-auto flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.14em]">
            <button
              onClick={() => setPaused(p => !p)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md cursor-pointer"
              style={{
                background: 'var(--shell-surface)', color: 'var(--shell-text-muted)',
                border: '1px solid var(--shell-border)',
              }}
              data-testid="capital-loop-toggle"
            >
              {paused ? <Play size={10} /> : <Pause size={10} />}
              {paused ? 'Play' : 'Pause'}
            </button>
            <button
              onClick={replay}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md cursor-pointer"
              style={{
                background: 'var(--shell-surface)', color: 'var(--shell-text-muted)',
                border: '1px solid var(--shell-border)',
              }}
              data-testid="capital-replay-btn"
            >
              <RotateCcw size={10} />
              Replay
            </button>
          </div>
        </div>

        {/* ═══ 4-STEP FLOW STRIP ═══ */}
        <div
          className="grid grid-cols-4 mb-4 rounded-lg overflow-hidden"
          style={{ background: 'var(--shell-surface)', border: '1px solid var(--shell-border)' }}
          data-testid="capital-flow-strip"
        >
          {FLOW_STEPS.map((step, i) => {
            const active = stage >= i;
            const current = stage === i;
            return (
              <div key={step.key} className="flex items-center gap-3 px-4 py-3 relative"
                style={{
                  borderRight: i < FLOW_STEPS.length - 1 ? '1px solid var(--shell-border)' : 'none',
                  background: current ? `${step.color}0A` : 'transparent',
                  transition: 'background 0.4s ease',
                }}
              >
                <motion.div
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{
                    background: active ? `${step.color}15` : 'var(--shell-surface)',
                    border: `1.5px solid ${active ? step.color : 'var(--shell-border)'}`,
                    color: active ? step.color : 'var(--shell-text-muted)',
                    fontFamily: "'SF Mono', monospace", fontSize: 13, fontWeight: 700,
                    boxShadow: current ? `0 0 16px ${step.color}40` : 'none',
                    flexShrink: 0,
                  }}
                  animate={current ? { scale: [1, 1.05, 1] } : { scale: 1 }}
                  transition={current ? { duration: 1.4, repeat: Infinity, ease: 'easeInOut' } : { duration: 0 }}
                >
                  {step.icon}
                </motion.div>
                <div className="min-w-0">
                  <p className="text-[12px] font-semibold" style={{ color: active ? 'var(--shell-text)' : 'var(--shell-text-muted)', letterSpacing: '-0.01em' }}>
                    {step.label}
                  </p>
                  <p className="text-[10px]" style={{ color: 'var(--shell-text-muted)' }}>
                    {step.blurb}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* ═══ MAP + SINGLE ACTIVE STORY PANEL ═══ */}
        <div className="grid lg:grid-cols-[1fr_340px] gap-6 mb-10">

          {/* MAP — all other regions muted, only active scenario pulses */}
          <div
            className="rounded-lg overflow-hidden relative"
            style={{
              background: 'linear-gradient(180deg, var(--shell-surface) 0%, var(--shell-bg) 100%)',
              border: '1px solid var(--shell-border)',
              minHeight: 380,
              boxShadow: '0 8px 28px rgba(0,0,0,0.04)',
            }}
            data-testid="cwc-chapter-map"
          >
            <ComposableMap projection="geoMercator" projectionConfig={{ scale: 135, center: [30, 10] }}
              style={{ width: '100%', height: '100%', minHeight: 380 }}>
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const regionId = ISO_NUMERIC_MAP[geo.id];
                    const region = regionId ? ATLAS_REGIONS.find(r => r.id === regionId) : null;
                    const isActiveCountry = regionId === story.countryISO;
                    const baseFill = region ? getNeedColor(region) : 'rgba(10,10,10,0.05)';
                    return (
                      <Geography key={geo.rsmKey} geography={geo}
                        style={{
                          default: {
                            fill: isActiveCountry ? S : baseFill,
                            stroke: isActiveCountry ? S : 'var(--g-border)',
                            strokeWidth: isActiveCountry ? 0.9 : 0.35,
                            outline: 'none',
                            opacity: isActiveCountry ? 1 : 0.55,
                            transition: 'opacity 0.4s, fill 0.4s',
                          },
                          hover: {
                            fill: isActiveCountry ? S : baseFill,
                            outline: 'none',
                            opacity: isActiveCountry ? 1 : 0.55,
                          },
                          pressed: { outline: 'none' },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
              {/* Other region dots (dimmed) */}
              {ATLAS_REGIONS.map((region) => {
                const isActiveCountry = region.id === story.countryISO;
                if (isActiveCountry) return null;
                return (
                  <Marker key={region.id} coordinates={region.center}>
                    <circle r={1.8} fill="var(--shell-text, #0A0A0A)" opacity={0.25} />
                  </Marker>
                );
              })}
              {/* ACTIVE STORY MARKER — big, animated, obviously the focus */}
              <Marker coordinates={story.coordinates}>
                <circle r={8} fill={stageColor} opacity={0.18}>
                  <animate attributeName="r" from="6" to="18" dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" from="0.4" to="0" dur="2s" repeatCount="indefinite" />
                </circle>
                <circle r={4.5} fill={stageColor} opacity={0.9} />
                <circle r={1.8} fill="#fff" opacity={0.95} />
              </Marker>
            </ComposableMap>

            {/* ACTIVE SCENARIO LABEL (top-left) */}
            <div
              className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold"
              style={{
                background: 'rgba(12,12,12,0.82)',
                color: '#fff',
                border: `1px solid ${S}55`,
                backdropFilter: 'blur(8px)',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: S, boxShadow: `0 0 6px ${S}` }} />
              {story.flag} {story.title}
            </div>

            {/* STAGE CHIP (bottom) */}
            <AnimatePresence mode="wait">
              {stage >= 0 && (
                <motion.div
                  key={stage}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  style={{ position: 'absolute', bottom: 14, left: 14, right: 14, display: 'flex', justifyContent: 'center' }}
                  data-testid="contradiction-chip"
                >
                  <span style={{
                    fontSize: 10, fontFamily: "'SF Mono', monospace", padding: '5px 12px', borderRadius: 999,
                    background: `${stageColor}18`,
                    border: `1px solid ${stageColor}50`,
                    color: stageColor, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
                    backdropFilter: 'blur(8px)',
                  }}>
                    {stage === 0 && '◼ Pledged'}
                    {stage === 1 && `✕ Trapped · ${story.contradiction}`}
                    {stage === 2 && '◐ Intervention in progress'}
                    {stage === 3 && '✓ Verified arrival'}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* RIGHT PANEL — single active story, walks through 4 stages */}
          <AnimatePresence mode="wait">
            <motion.div
              key={story.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
              className="flex flex-col gap-3"
              data-testid="active-story-panel"
            >
              {/* Header */}
              <div className="px-1">
                <p className="text-[9px] font-mono uppercase tracking-[0.22em] mb-1.5" style={{ color: 'var(--shell-text-muted)' }}>
                  {story.route}
                </p>
                <h3 className="text-[18px] font-semibold" style={{ color: 'var(--shell-text)', letterSpacing: '-0.02em', fontFamily: '"Source Serif 4", Georgia, serif' }}>
                  {story.flag} {story.title}
                </h3>
                <p className="text-[12px] mt-1" style={{ color: 'var(--shell-text-muted)' }}>
                  {story.need}
                </p>
              </div>

              {/* 4 stage blocks — vertical narrative */}
              <div className="flex flex-col gap-2 mt-1">
                <StageBlock
                  active={stage >= 0}
                  color="var(--shell-text)"
                  label="Pledged"
                  num={1}
                  body={<>Budget announced. <span style={{ color: 'var(--shell-text-muted)' }}>Awaiting deployment.</span></>}
                />
                <StageBlock
                  active={stage >= 1}
                  current={stage === 1}
                  color={S}
                  label="Trapped"
                  num={2}
                  body={<><span style={{ color: S, fontWeight: 600 }}>{story.contradiction}.</span> No verified arrival point.</>}
                  metricLabel="Trapped"
                  metricValue={animTrapped > 0 ? `$${animTrapped.toFixed(1)}M` : '$0'}
                  metricColor={animTrapped > 0 ? S : 'var(--shell-text-muted)'}
                />
                <StageBlock
                  active={stage >= 2}
                  current={stage === 2}
                  color={G}
                  label="Intervention"
                  num={3}
                  body={story.resolution}
                  metricLabel="Nodes"
                  metricValue={animNodes > 0 ? animNodes.toLocaleString() : '—'}
                  metricColor={animNodes > 0 ? G : 'var(--shell-text-muted)'}
                />
                <StageBlock
                  active={stage >= 3}
                  current={stage === 3}
                  color={E}
                  label="Verified"
                  num={4}
                  body={<span style={{ color: E, fontWeight: 600 }}>{story.outcome}</span>}
                />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ═══ TODAY vs WITH GRANULLO ═══ */}
        <div className="grid sm:grid-cols-2 gap-4 mb-10" data-testid="today-vs-granullo">
          <motion.div
            whileHover={{ y: -2 }}
            className="p-4 rounded-lg"
            style={{ background: `${S}05`, border: `1px solid ${S}18`, transition: 'all 200ms ease' }}
          >
            <p className="text-[9px] font-mono uppercase tracking-[0.16em] mb-3 flex items-center gap-1.5" style={{ color: S, fontWeight: 700 }}>
              <AlertTriangle size={11} /> Today
            </p>
            <p className="text-[13px] leading-[1.6]" style={{ color: 'var(--shell-text-secondary)' }}>
              Budget reaches the district. <strong style={{ color: 'var(--shell-text)' }}>Need never gets coordinates.</strong>
              No proof of who received what. Capital leaks between program and person.
            </p>
          </motion.div>
          <motion.div
            whileHover={{ y: -2 }}
            className="p-4 rounded-lg"
            style={{ background: `${E}05`, border: `1px solid ${E}22`, transition: 'all 200ms ease' }}
          >
            <p className="text-[9px] font-mono uppercase tracking-[0.16em] mb-3 flex items-center gap-1.5" style={{ color: E, fontWeight: 700 }}>
              <Shield size={11} /> With Granullo
            </p>
            <p className="text-[13px] leading-[1.6]" style={{ color: 'var(--shell-text-secondary)' }}>
              Every village, institution, and beneficiary becomes a <strong style={{ color: 'var(--shell-text)' }}>verified arrival node</strong>.
              Capital releases only after structural proof of outcome. Leakage prevented at the last mile.
            </p>
          </motion.div>
        </div>

        {/* ═══ CTA STRIP ═══ */}
        <div
          className="rounded-lg px-5 py-5 flex flex-col md:flex-row md:items-center justify-between gap-4"
          style={{ background: 'var(--shell-surface)', border: '1px solid var(--shell-border)' }}
          data-testid="cwc-cta-strip"
        >
          <div className="max-w-[540px]">
            <p className="text-[13px] sm:text-[14px] font-semibold leading-[1.5]" style={{ color: 'var(--shell-text)', letterSpacing: '-0.01em' }}>
              See the live atlas for your program.
            </p>
            <p className="text-[12px] mt-0.5" style={{ color: 'var(--shell-text-muted)' }}>
              Map your committed capital against verified arrival nodes in under 10 minutes.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <motion.button
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/intelligence/capital-with-coordinates')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-[13px] font-semibold"
              style={{ background: 'var(--shell-text)', color: 'var(--shell-bg)', border: 'none', cursor: 'pointer', letterSpacing: '-0.01em' }}
              data-testid="cwc-explore-atlas-btn"
            >
              Explore the live atlas <ArrowRight size={14} />
            </motion.button>
            <motion.button
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/intelligence/capital-with-coordinates#foundation')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-[13px] font-semibold"
              style={{ background: 'transparent', color: S, border: `1px solid ${S}66`, cursor: 'pointer', letterSpacing: '-0.01em' }}
              data-testid="cwc-map-program-btn"
            >
              Map my program
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Single-stage narrative block ─── */
function StageBlock({ active, current, color, label, num, body, metricLabel, metricValue, metricColor }) {
  return (
    <motion.div
      className="relative rounded-lg px-3.5 py-3 flex items-start gap-3"
      animate={{
        background: current
          ? `${color === 'var(--shell-text)' ? 'rgba(0,0,0,0.04)' : `${color}0C`}`
          : 'var(--shell-surface)',
        borderColor: current ? (color === 'var(--shell-text)' ? 'rgba(0,0,0,0.3)' : `${color}55`) : 'var(--shell-border)',
        opacity: active ? 1 : 0.45,
      }}
      style={{
        border: '1px solid',
        transition: 'all 320ms ease',
      }}
    >
      {/* Step number */}
      <div className="shrink-0 flex flex-col items-center gap-1 pt-0.5">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-mono font-bold"
          style={{
            background: active ? (color === 'var(--shell-text)' ? 'rgba(0,0,0,0.08)' : `${color}14`) : 'var(--shell-border)',
            border: `1px solid ${active ? (color === 'var(--shell-text)' ? 'rgba(0,0,0,0.35)' : `${color}55`) : 'transparent'}`,
            color: active ? (color === 'var(--shell-text)' ? 'var(--shell-text)' : color) : 'var(--shell-text-muted)',
          }}
        >
          {num}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2 mb-0.5">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] font-semibold"
            style={{ color: active ? (color === 'var(--shell-text)' ? 'var(--shell-text)' : color) : 'var(--shell-text-muted)' }}
          >
            {label}
          </p>
          {metricValue && (
            <span className="text-[11px] font-mono tabular-nums font-semibold"
              style={{ color: metricColor }}
            >
              {metricValue}
              {metricLabel && (
                <span className="text-[9px] font-normal uppercase tracking-[0.12em] ml-1" style={{ color: 'var(--shell-text-muted)' }}>
                  {metricLabel}
                </span>
              )}
            </span>
          )}
        </div>
        <p className="text-[11.5px] leading-[1.5]" style={{ color: active ? 'var(--shell-text)' : 'var(--shell-text-muted)' }}>
          {body}
        </p>
      </div>
    </motion.div>
  );
}
